import { GRACE_WINDOW_HOURS, SEVERITIES, type FixKind, type IssueDetail, type IssueEvent, type IssueState, type IssueSummary, type Severity } from "@ti/core";
import { z } from "zod";

// ─── URL search params ───────────────────────────────────────────────────────

export const ISSUE_VIEWS = ["list", "board"] as const;
export type IssueView = (typeof ISSUE_VIEWS)[number];

const IssuesSearchSchema = z.object({
  view: z.enum(ISSUE_VIEWS).optional().catch(undefined),
  severity: z.enum(SEVERITIES).optional().catch(undefined),
  state: z.enum(["detected", "grace", "mitigated", "verifying", "resolved", "excluded", "regressed"]).optional().catch(undefined),
  agentId: z.string().min(1).optional().catch(undefined),
});
export type IssuesSearch = z.output<typeof IssuesSearchSchema>;

/** Lenient: unknown or malformed params are dropped rather than throwing. */
export function parseIssuesSearch(search: Record<string, unknown>): IssuesSearch {
  const r = IssuesSearchSchema.safeParse(search);
  if (!r.success) return {};
  // Strip undefined keys so links stay clean.
  return Object.fromEntries(Object.entries(r.data).filter(([, v]) => v !== undefined)) as IssuesSearch;
}

// ─── Grouping & ordering ─────────────────────────────────────────────────────

export const SEVERITY_RANK: Record<Severity, number> = { critical: 0, high: 1, medium: 2, low: 3 };

export const OPEN_STATES: ReadonlySet<IssueState> = new Set(["detected", "grace", "regressed", "mitigated", "verifying", "excluded"]);
/** States where the builder still owes a fix (a fix version can be declared). */
export const FIXABLE_STATES: ReadonlySet<IssueState> = new Set(["detected", "grace", "regressed", "mitigated", "excluded"]);

export type BoardColumnKey = "detected" | "grace" | "mitigated" | "verifying" | "resolved" | "excluded";

export const BOARD_COLUMNS: Array<{ key: BoardColumnKey; label: string; hint: string }> = [
  { key: "detected", label: "Detected", hint: "Awaiting triage — grace starts on confirmation" },
  { key: "grace", label: "Grace", hint: "Confirmed, covered while the clock runs" },
  { key: "mitigated", label: "Mitigated", hint: "Control enforced by the agent platform — covered while active" },
  { key: "verifying", label: "Verifying", hint: "Fix declared — replay, probes and soak running" },
  { key: "resolved", label: "Resolved", hint: "Verified fix — scenario covered" },
  { key: "excluded", label: "Excluded", hint: "Grace expired — graduated or excluded" },
];

export function boardColumnOf(state: IssueState): BoardColumnKey {
  return state === "regressed" ? "grace" : state;
}

function deadlineMs(i: IssueSummary) {
  return i.graceDeadline ? Date.parse(i.graceDeadline) : Number.POSITIVE_INFINITY;
}

/** Severity first, then the nearest grace deadline. */
export function bySeverityThenDeadline(a: IssueSummary, b: IssueSummary) {
  return SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || deadlineMs(a) - deadlineMs(b) || b.lastSeen.localeCompare(a.lastSeen);
}

/** List ordering: open issues first, then severity, then deadline. */
export function byOpenSeverityDeadline(a: IssueSummary, b: IssueSummary) {
  return Number(!OPEN_STATES.has(a.state)) - Number(!OPEN_STATES.has(b.state)) || bySeverityThenDeadline(a, b);
}

// ─── Grace ───────────────────────────────────────────────────────────────────

/** True when the issue has a running grace clock worth showing. */
export function hasActiveGrace(i: Pick<IssueSummary, "state" | "graceStartedAt" | "graceDeadline">) {
  return Boolean(i.graceStartedAt && i.graceDeadline) && (i.state === "grace" || i.state === "regressed" || i.state === "verifying");
}

/** Fraction of the grace window remaining (0..1), or null if no active window. */
export function graceFractionLeft(i: Pick<IssueSummary, "state" | "graceStartedAt" | "graceDeadline">, now = Date.now()) {
  if (!hasActiveGrace(i)) return null;
  const start = Date.parse(i.graceStartedAt!);
  const end = Date.parse(i.graceDeadline!);
  return Math.max(0, Math.min(1, (end - now) / Math.max(1, end - start)));
}

export function isInFinalStretch(i: IssueSummary, now = Date.now()) {
  const f = graceFractionLeft(i, now);
  return f !== null && f < 0.2;
}

export function graceWindowLabel(severity: Severity) {
  const h = GRACE_WINDOW_HOURS[severity];
  return h < 72 ? `${h}h` : `${h / 24}-day`;
}

// ─── Lifecycle stepper ───────────────────────────────────────────────────────

export type StepStatus = "done" | "current" | "upcoming" | "skipped" | "alert";
export interface LifecycleStep {
  key: string;
  label: string;
  status: StepStatus;
  at?: string;
  note?: string;
}

const STEP_EVENTS: Record<string, IssueEvent["type"][]> = {
  detected: ["detected"],
  notified: ["notified", "grace_started"],
  mitigated: ["mitigated"],
  verifying: ["fix_declared"],
  resolved: ["resolved"],
  excluded: ["excluded"],
  regressed: ["regressed"],
};

const CURRENT_STEP: Record<IssueState, string> = {
  detected: "detected",
  grace: "notified",
  mitigated: "mitigated",
  verifying: "verifying",
  resolved: "resolved",
  excluded: "excluded",
  regressed: "regressed",
};

function firstEvent(timeline: IssueEvent[], types: IssueEvent["type"][]) {
  return timeline.find((e) => types.includes(e.type));
}

/**
 * Detected → Notified → Mitigated → Verifying → Resolved, plus "Grace expired" (excluded) and
 * "Regressed" variants. Timestamps come from the timeline; steps passed without an event are
 * shown as skipped (e.g. an issue fixed without a compensating control).
 */
export function lifecycleSteps(issue: IssueDetail): LifecycleStep[] {
  const keys = ["detected", "notified", "mitigated", "verifying", "resolved"];
  if (issue.state === "excluded") keys.splice(2, 0, "excluded");
  if (issue.state === "regressed") keys.push("regressed");

  const labels: Record<string, string> = {
    detected: "Detected",
    notified: "Notified · grace started",
    mitigated: "Mitigated",
    verifying: "Verifying",
    resolved: "Resolved",
    excluded: issue.coverageState === "graduated" ? "Grace expired · graduated" : "Grace expired · excluded",
    regressed: "Regressed",
  };

  const current = CURRENT_STEP[issue.state];
  const currentIdx = keys.indexOf(current);

  return keys.map((key, idx) => {
    const ev = firstEvent(issue.timeline, STEP_EVENTS[key] ?? []);
    let status: StepStatus;
    if (key === current) status = key === "excluded" || key === "regressed" ? "alert" : issue.state === "resolved" ? "done" : "current";
    else if (idx < currentIdx) status = ev ? "done" : "skipped";
    else status = "upcoming";
    return { key, label: labels[key] ?? key, status, at: status === "upcoming" ? undefined : ev?.at, note: ev?.note };
  });
}

// ─── Fixes ───────────────────────────────────────────────────────────────────

export const FIX_ORDER: Record<FixKind, number> = { control: 0, prompt_patch: 1, tool_fix: 2, probes: 3 };
export const FIX_KIND_LABEL: Record<FixKind, string> = {
  control: "Platform control",
  prompt_patch: "Prompt patch",
  tool_fix: "Tool fix",
  probes: "Regression probes",
};

/**
 * Probe bodies are newline-separated ids; a compact range like "probe_212_01 … probe_212_12"
 * is expanded so every probe is listed.
 */
export function parseProbes(body: string): string[] {
  const out: string[] = [];
  for (const raw of body.split(/\r?\n/)) {
    const line = raw.trim().replace(/^[-*•]\s*/, "");
    if (!line) continue;
    const m = /^(.*?)(\d+)\s*(?:…|\.\.\.|–|-|to)\s*(.*?)(\d+)$/.exec(line);
    if (m && m[1] === m[3] && m[2] && m[4]) {
      const from = Number(m[2]);
      const to = Number(m[4]);
      const width = m[2].length;
      if (to >= from && to - from < 500) {
        for (let n = from; n <= to; n++) out.push(`${m[1]}${String(n).padStart(width, "0")}`);
        continue;
      }
    }
    out.push(line);
  }
  return out;
}

/** Suggest the next version label, e.g. v58 → v59. */
export function nextVersion(versions: string[]) {
  const last = versions[versions.length - 1];
  if (!last) return "";
  const m = /^(.*?)(\d+)$/.exec(last);
  return m ? `${m[1]}${Number(m[2]) + 1}` : "";
}
